import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Copy,
  ExternalLink,
  ImageOff,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  FaFacebook,
  FaLinkedin,
  FaWhatsapp,
  FaDiscord,
  FaCode,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { toast } from "react-toastify";
import { cn } from "../../lib/cn";
import AiModelModal from "../modals/AiModelModal";
import { DEFAULT_AI_MODEL, getAiModelLabel, getAiModelTierBadge } from "../../constants/aiModels";
import { useAuth } from "../modals/AuthContext";
import { useAuthModal } from "../modals/AuthModalContext";
import {
  buildResolvedFromTags,
  isAiImprovedField,
  mergeSocialTags,
} from "../../utils/socialMetaMerge";
import {
  getCharCountStatus,
  META_CHAR_LIMITS,
} from "../../constants/socialMetaLimits";
import { buildSeoContext } from "../../utils/buildSeoContext";

const PLATFORMS = [
  { id: "facebook", label: "Facebook", Icon: FaFacebook },
  { id: "x", label: "X", Icon: FaXTwitter },
  { id: "linkedin", label: "LinkedIn", Icon: FaLinkedin },
  { id: "whatsapp", label: "WhatsApp", Icon: FaWhatsapp },
  { id: "discord", label: "Discord", Icon: FaDiscord },
  { id: "code", label: "Code", Icon: FaCode },
];

function StatusIcon({ status }) {
  if (status === "pass") {
    return (
      <CheckCircle2
        size={16}
        className="text-green-600 shrink-0"
        aria-label="Pass"
      />
    );
  }
  if (status === "warn") {
    return (
      <AlertTriangle
        size={16}
        className="text-amber-600 shrink-0"
        aria-label="Warning"
      />
    );
  }
  return (
    <AlertCircle
      size={16}
      className="text-red-600 shrink-0"
      aria-label="Error"
    />
  );
}

function CountBadge({ status, count }) {
  if (!count) return null;
  const styles = {
    pass: "bg-green-50 text-green-700 border-green-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    error: "bg-red-50 text-red-700 border-red-200",
  };
  const labels = { pass: "Passed", warn: "Warnings", error: "Errors" };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        styles[status],
      )}
    >
      <StatusIcon status={status} />
      {count} {labels[status]}
    </span>
  );
}

function PreviewImage({
  src,
  alt,
  fit = "cover",
  aspectClass,
  className,
}) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-2 bg-[#1a1a1a] text-white/50",
          aspectClass,
          className,
        )}
      >
        <ImageOff size={24} strokeWidth={1.5} />
        <span className="text-[10px]">No image</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn(
        "bg-[#1a1a1a]",
        fit === "cover" ? "object-cover" : "object-contain",
        aspectClass,
        className,
      )}
      onError={() => setFailed(true)}
    />
  );
}

function ThumbImage({ src, alt, size, className }) {
  const [failed, setFailed] = useState(false);
  const sizeStyle =
    size != null && !className ? { width: size, height: size } : undefined;

  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden bg-[#1a1a1a]",
        className,
      )}
      style={sizeStyle}
    >
      {!src || failed ? (
        <div className="flex h-full w-full items-center justify-center text-white/40">
          <ImageOff size={20} strokeWidth={1.5} />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

function PreviewStage({ children, className }) {
  return (
    <div
      className={cn(
        "flex justify-center bg-[#f0f2f5] px-2 py-3 sm:px-4 sm:py-5",
        className,
      )}
    >
      <div className="w-full min-w-0 flex justify-center">{children}</div>
    </div>
  );
}

/** Facebook large link card – 1.91:1 image, domain + title footer (feed style). */
function FacebookCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const domain = (resolved.domain || "example.com").toUpperCase();

  return (
    <PreviewStage>
      <div className="w-full max-w-[500px] overflow-hidden rounded-lg border border-[#dddfe2] bg-white">
        <PreviewImage
          src={resolved.image}
          alt=""
          aspectClass="w-full aspect-[1.91/1]"
        />
        <div className="bg-[#f2f3f5] px-3 py-2.5">
          <p className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wide text-[#606770] truncate">
            {domain}
          </p>
          <p className="mt-0.5 text-[15px] sm:text-[16px] font-semibold text-[#1c1e21] line-clamp-2 leading-snug">
            {title}
          </p>
        </div>
      </div>
    </PreviewStage>
  );
}

/** X summary_large_image – wide image on top, title + description + domain below. */
function XLargeCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const description = resolved.description || "";
  const domain = resolved.domain || "example.com";

  return (
    <PreviewStage>
      <div className="w-full max-w-[504px] overflow-hidden rounded-2xl border border-[#cfd9de] bg-white">
        <PreviewImage
          src={resolved.image}
          alt=""
          aspectClass="w-full aspect-[1.91/1]"
        />
        <div className="px-3 py-2.5">
          <p className="text-[14px] sm:text-[15px] font-normal text-[#0f1419] line-clamp-2 leading-snug">
            {title}
          </p>
          {description && (
            <p className="mt-0.5 text-[14px] sm:text-[15px] text-[#536471] line-clamp-2 leading-snug">
              {description}
            </p>
          )}
          <p className="mt-1 flex items-center gap-1 text-[14px] sm:text-[15px] text-[#536471]">
            <span className="truncate">{domain}</span>
          </p>
        </div>
      </div>
    </PreviewStage>
  );
}

/** X summary card – small square thumb left, text right. */
function XSummaryCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const description = resolved.description || "";
  const domain = resolved.domain || "example.com";

  return (
    <PreviewStage>
      <div className="w-full max-w-[504px] overflow-hidden rounded-2xl border border-[#cfd9de] bg-white">
        <div className="sm:hidden">
          <PreviewImage
            src={resolved.image}
            alt=""
            aspectClass="w-full aspect-[1.91/1]"
          />
          <div className="px-3 py-2.5">
            <p className="text-[14px] text-[#0f1419] line-clamp-2 leading-snug">
              {title}
            </p>
            {description && (
              <p className="mt-0.5 text-[14px] text-[#536471] line-clamp-2 leading-snug">
                {description}
              </p>
            )}
            <p className="mt-0.5 text-[14px] text-[#536471] truncate">
              {domain}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex">
          <ThumbImage src={resolved.image} alt="" size={106} />
          <div className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2">
            <p className="text-[15px] text-[#0f1419] line-clamp-2 leading-snug">
              {title}
            </p>
            {description && (
              <p className="mt-0.5 text-[15px] text-[#536471] line-clamp-2 leading-snug">
                {description}
              </p>
            )}
            <p className="mt-0.5 text-[15px] text-[#536471] truncate">
              {domain}
            </p>
          </div>
        </div>
      </div>
    </PreviewStage>
  );
}

function XCard({ resolved, tags }) {
  const cardType = tags?.twitterCard || "summary_large_image";
  if (cardType === "summary") {
    return <XSummaryCard resolved={resolved} />;
  }
  return <XLargeCard resolved={resolved} />;
}

/** LinkedIn organic link post – side thumbnail (2024+ feed layout). */
function LinkedInCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const domain = resolved.domain || "example.com";

  return (
    <PreviewStage className="bg-white">
      <div className="w-full max-w-[552px] overflow-hidden rounded-sm border border-[#e0e0e0] bg-[#fafafa]">
        <div className="sm:hidden">
          <PreviewImage
            src={resolved.image}
            alt=""
            aspectClass="w-full aspect-[1.91/1]"
          />
          <div className="px-3 py-2.5">
            <p className="text-[13px] font-semibold text-[#000000de] line-clamp-3 leading-snug">
              {title}
            </p>
            <p className="mt-1 text-[11px] text-[#00000099] truncate">
              {domain}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex">
          <ThumbImage
            src={resolved.image}
            alt=""
            className="w-[168px] h-[168px]"
          />
          <div className="flex min-w-0 flex-1 flex-col justify-center border-l border-[#e0e0e0] px-3 py-2.5">
            <p className="text-[14px] font-semibold text-[#000000de] line-clamp-2 leading-snug">
              {title}
            </p>
            <p className="mt-1 text-[12px] text-[#00000099] truncate">
              {domain}
            </p>
          </div>
        </div>
      </div>
    </PreviewStage>
  );
}

/** WhatsApp mobile link bubble – image, title, description, domain. */
function WhatsAppCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const description =
    resolved.description || "No description available.";
  const domain = resolved.domain || "example.com";

  return (
    <PreviewStage className="bg-[#efeae2]">
      <div className="w-full max-w-[300px] overflow-hidden rounded-lg border border-[#e9edef] bg-white shadow-sm">
        <PreviewImage
          src={resolved.image}
          alt=""
          aspectClass="w-full aspect-[1.91/1]"
        />
        <div className="px-3 py-2.5">
          <p className="text-[13px] sm:text-[14px] font-medium text-[#111b21] line-clamp-2 leading-snug">
            {title}
          </p>
          <p className="mt-1 text-[12px] sm:text-[13px] text-[#667781] line-clamp-3 leading-relaxed">
            {description}
          </p>
          <p className="mt-2 text-[11px] sm:text-[12px] text-[#8696a0] truncate">
            {domain}
          </p>
        </div>
      </div>
    </PreviewStage>
  );
}

/** Discord large embed – title, description, then full-width image (landscape OG). */
function DiscordCard({ resolved }) {
  const title = resolved.title || "Untitled";
  const description =
    resolved.description || "No description available.";
  const site = resolved.domain || "example.com";

  return (
    <PreviewStage className="bg-[#36393f]">
      <div className="w-full max-w-[432px] overflow-hidden rounded-[4px] border-l-4 border-l-[#5865f2] bg-[#2b2d31]">
        <div className="px-3 pt-2.5 pb-2">
          <p className="text-[11px] sm:text-[12px] font-medium text-[#b5bac1]">
            {site}
          </p>
          <p className="mt-0.5 text-[15px] sm:text-[16px] font-semibold text-[#00aff4] line-clamp-2 leading-snug hover:underline cursor-default">
            {title}
          </p>
          <p className="mt-1 text-[13px] sm:text-[14px] text-[#dbdee1] line-clamp-4 leading-relaxed">
            {description}
          </p>
        </div>
        <PreviewImage
          src={resolved.image}
          alt=""
          aspectClass="w-full aspect-[1.91/1]"
        />
      </div>
    </PreviewStage>
  );
}

function escapeAttr(value) {
  return (value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function buildMetaSnippet(tags, resolved) {
  const title = tags.title || resolved.title || "";
  const description = tags.description || resolved.description || "";
  const ogTitle = tags.ogTitle || title;
  const ogDescription = tags.ogDescription || description;
  const ogImage = tags.ogImage || tags.twitterImage || resolved.image || "";
  const ogUrl = tags.ogUrl || resolved.url || "";
  const twitterCard = tags.twitterCard || "summary_large_image";
  const twitterTitle = tags.twitterTitle || ogTitle;
  const twitterDescription = tags.twitterDescription || ogDescription;
  const twitterImage = tags.twitterImage || ogImage;

  const lines = [];
  if (title) lines.push({ tag: "title", content: title });
  if (description) {
    lines.push({
      tag: 'meta name="description"',
      attr: "content",
      content: description,
    });
  }
  if (ogTitle) {
    lines.push({
      tag: 'meta property="og:title"',
      attr: "content",
      content: ogTitle,
    });
  }
  if (ogDescription) {
    lines.push({
      tag: 'meta property="og:description"',
      attr: "content",
      content: ogDescription,
    });
  }
  if (ogImage) {
    lines.push({
      tag: 'meta property="og:image"',
      attr: "content",
      content: ogImage,
    });
    lines.push({
      tag: 'meta property="og:image:width"',
      attr: "content",
      content: "1200",
    });
    lines.push({
      tag: 'meta property="og:image:height"',
      attr: "content",
      content: "630",
    });
  }
  if (ogUrl) {
    lines.push({
      tag: 'meta property="og:url"',
      attr: "content",
      content: ogUrl,
    });
  }
  if (tags.ogSiteName) {
    lines.push({
      tag: 'meta property="og:site_name"',
      attr: "content",
      content: tags.ogSiteName,
    });
  }
  if (twitterCard) {
    lines.push({
      tag: 'meta name="twitter:card"',
      attr: "content",
      content: twitterCard,
    });
  }
  if (twitterTitle) {
    lines.push({
      tag: 'meta name="twitter:title"',
      attr: "content",
      content: twitterTitle,
    });
  }
  if (twitterDescription) {
    lines.push({
      tag: 'meta name="twitter:description"',
      attr: "content",
      content: twitterDescription,
    });
  }
  if (twitterImage) {
    lines.push({
      tag: 'meta name="twitter:image"',
      attr: "content",
      content: twitterImage,
    });
  }

  return lines;
}

function MetaCodeLine({ line }) {
  if (line.tag === "title") {
    return (
      <div className="leading-relaxed">
        <span className="text-[#c792ea]">&lt;</span>
        <span className="text-[#f07178]">title</span>
        <span className="text-[#c792ea]">&gt;</span>
        <span className="text-[#d6deeb]">{line.content}</span>
        <span className="text-[#c792ea]">&lt;/</span>
        <span className="text-[#f07178]">title</span>
        <span className="text-[#c792ea]">&gt;</span>
      </div>
    );
  }

  return (
    <div className="leading-relaxed break-all">
      <span className="text-[#c792ea]">&lt;</span>
      <span className="text-[#f07178]">{line.tag}</span>
      <span className="text-[#c792ea]"> </span>
      <span className="text-[#ffcb6b]">{line.attr}</span>
      <span className="text-[#c792ea]">=</span>
      <span className="text-[#c3e88d]">&quot;{line.content}&quot;</span>
      <span className="text-[#c792ea]"> /&gt;</span>
    </div>
  );
}

function CodeCard({ tags, resolved }) {
  const lines = buildMetaSnippet(tags, resolved);
  const plainText = lines
    .map((line) => {
      if (line.tag === "title") {
        return `<title>${line.content}</title>`;
      }
      return `<${line.tag} ${line.attr}="${escapeAttr(line.content)}" />`;
    })
    .join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      toast.success("Meta tags copied to clipboard");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <div className="relative bg-[#1e1e2e] px-4 py-5 sm:px-5 sm:py-6">
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-3 right-3 rounded-md p-2 text-[#a6accd] hover:bg-white/10 hover:text-white transition-colors"
        aria-label="Copy meta tags"
        title="Copy"
      >
        <Copy size={16} />
      </button>
      <pre className="overflow-x-auto pr-10 font-mono text-[12px] sm:text-[13px]">
        {lines.length === 0 ? (
          <span className="text-[#a6accd]">No meta tags found.</span>
        ) : (
          lines.map((line, i) => <MetaCodeLine key={i} line={line} />)
        )}
      </pre>
    </div>
  );
}

const PREVIEW_BY_PLATFORM = {
  facebook: FacebookCard,
  x: XCard,
  linkedin: LinkedInCard,
  whatsapp: WhatsAppCard,
  discord: DiscordCard,
};

function PlatformPreviewTabs({ resolved, tags }) {
  const [active, setActive] = useState("facebook");
  const Preview = PREVIEW_BY_PLATFORM[active];

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <div
        className="flex items-stretch overflow-x-auto border-b border-border bg-white scrollbar-none"
        role="tablist"
        aria-label="Social platform previews"
      >
        {PLATFORMS.map(({ id, label, Icon }) => {
          const selected = active === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-label={label}
              title={label}
              onClick={() => setActive(id)}
              className={cn(
                "relative flex min-w-[3.25rem] flex-1 items-center justify-center py-3 sm:min-w-0 sm:py-3.5 transition-colors",
                "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset",
                selected
                  ? "text-foreground"
                  : "text-[#65676b] hover:text-foreground/80",
              )}
            >
              <Icon size={18} aria-hidden />
              <span
                className={cn(
                  "absolute bottom-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full transition-colors",
                  selected ? "bg-brand" : "bg-transparent",
                )}
                aria-hidden
              />
            </button>
          );
        })}
      </div>

      <div role="tabpanel" aria-label={`${active} preview`} className="min-h-0">
        {active === "code" ? (
          <CodeCard tags={tags} resolved={resolved} />
        ) : (
          Preview && <Preview resolved={resolved} tags={tags} />
        )}
      </div>
    </div>
  );
}

const TAG_ROWS = [
  { key: "title", label: "title", charLimit: META_CHAR_LIMITS.title },
  { key: "description", label: "description", charLimit: META_CHAR_LIMITS.description },
  { key: "ogTitle", label: "og:title", charLimit: META_CHAR_LIMITS.ogTitle },
  { key: "ogDescription", label: "og:description", charLimit: META_CHAR_LIMITS.ogDescription },
  { key: "ogImage", label: "og:image" },
  { key: "ogUrl", label: "og:url" },
  { key: "ogSiteName", label: "og:site_name", charLimit: META_CHAR_LIMITS.ogSiteName },
  { key: "ogType", label: "og:type" },
  { key: "twitterCard", label: "twitter:card" },
  { key: "twitterTitle", label: "twitter:title", charLimit: META_CHAR_LIMITS.twitterTitle },
  { key: "twitterDescription", label: "twitter:description", charLimit: META_CHAR_LIMITS.twitterDescription },
  { key: "twitterImage", label: "twitter:image" },
];

function MetaCharCount({ value, max }) {
  if (max == null || !value) return null;
  const len = value.length;
  const status = getCharCountStatus(len, max);
  return (
    <span
      className={cn(
        "text-[10px] font-mono tabular-nums shrink-0",
        status === "error" && "text-red-600",
        status === "warn" && "text-amber-600",
        status === "ok" && "text-muted-foreground",
      )}
    >
      {len}/{max}
    </span>
  );
}

function formatAiUsageHint(aiUsage) {
  if (!aiUsage || aiUsage.unlimited) return null;
  if (aiUsage.remaining === 0) {
    return "Limit reached today";
  }
  return `${aiUsage.remaining}/${aiUsage.limit} left today`;
}

function SocialAiBanner({
  improving,
  aiTags,
  aiDisabled,
  isAuthenticated,
  aiUsage,
  onFix,
  metaView,
  onMetaViewChange,
  needsFix,
}) {
  if (aiDisabled) return null;

  if (improving) {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-foreground">
        <Loader2 size={16} className="animate-spin text-brand shrink-0" />
        Improving meta tags for social previews…
      </div>
    );
  }

  if (aiTags) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-brand font-medium">
          <Sparkles size={12} />
          Meta tags improved for social previews
        </span>
        <button
          type="button"
          onClick={() =>
            onMetaViewChange(metaView === "original" ? "ai" : "original")
          }
          className="text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          {metaView === "original" ? "View improved" : "View original"}
        </button>
      </div>
    );
  }

  const atLimit = aiUsage && !aiUsage.unlimited && aiUsage.remaining === 0;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-3">
      <p className="text-sm text-foreground">
        {isAuthenticated
          ? atLimit
            ? "You've used all meta improvements for today."
            : needsFix
              ? "Improve meta tags for better social previews."
              : "Polish meta tags for stronger social previews."
          : "Sign in to improve meta tags for social previews."}
      </p>
      <button
        type="button"
        onClick={onFix}
        disabled={atLimit}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium shrink-0",
          "bg-brand text-brand-foreground hover:bg-brand/90 transition-colors",
          "disabled:opacity-50 disabled:pointer-events-none",
        )}
      >
        <Sparkles size={14} />
        {isAuthenticated ? "Improve with AI" : "Sign in & improve"}
      </button>
    </div>
  );
}

function MetaInspector({
  tags,
  checks,
  baseTags,
  aiTags,
  metaView,
  aiDisabled,
  aiModel,
  onChangeModel,
  onRewrite,
  improving,
  aiUsage,
  isAuthenticated,
  hideAuditIssues = false,
}) {
  const checkByTag = Object.fromEntries(checks.map((c) => [c.tag, c]));
  const tierBadge = getAiModelTierBadge(aiModel);
  const modelLabel = getAiModelLabel(aiModel);
  const usageHint =
    isAuthenticated && !aiDisabled ? formatAiUsageHint(aiUsage) : null;
  const rewriteDisabled =
    improving ||
    (aiUsage && !aiUsage.unlimited && aiUsage.remaining === 0);

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3 border-b border-border bg-muted/30">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground shrink-0">
          Meta tags
        </p>
        {!aiDisabled && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
            {onChangeModel ? (
              <button
                type="button"
                onClick={onChangeModel}
                title={`${aiModel}. Click to change model.`}
                aria-label="Change AI model"
                aria-haspopup="dialog"
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-medium shrink-0",
                  "text-brand hover:underline underline-offset-2",
                  "focus:outline-none",
                )}
              >
                {modelLabel}
                {tierBadge && (
                  <span className="inline-flex items-center rounded-full bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand no-underline">
                    {tierBadge}
                  </span>
                )}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-brand">
                {modelLabel}
                {tierBadge && (
                  <span className="inline-flex items-center rounded-full bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                    {tierBadge}
                  </span>
                )}
              </span>
            )}
            {usageHint && (
              <span className="text-xs text-muted-foreground shrink-0">
                {usageHint}
              </span>
            )}
            {aiTags && (
              <button
                type="button"
                onClick={onRewrite}
                disabled={rewriteDisabled}
                title={
                  rewriteDisabled && !improving
                    ? "Daily meta improve limit reached"
                    : undefined
                }
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-medium shrink-0",
                  "text-brand hover:underline underline-offset-2",
                  "focus:outline-none",
                  "disabled:opacity-50 disabled:pointer-events-none",
                )}
              >
                {improving ? (
                  <>
                    <Loader2 size={12} className="animate-spin shrink-0" />
                    Rewriting…
                  </>
                ) : (
                  <>
                    <Sparkles size={12} className="shrink-0" />
                    Rewrite
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
      <ul className="divide-y divide-border">
        {TAG_ROWS.map(({ key, label, charLimit }) => {
          const value = tags[key];
          const check = checkByTag[label];
          const aiField =
            metaView === "ai" && isAiImprovedField(key, baseTags, aiTags);
          return (
            <li
              key={key}
              className={cn(
                "px-3 py-3 sm:px-4 flex gap-2 sm:gap-3 items-start text-sm",
                aiField && "bg-brand/[0.04]",
              )}
            >
              {check && !hideAuditIssues ? (
                <StatusIcon status={check.status} />
              ) : (
                <span className="w-4 shrink-0" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-mono text-xs text-muted-foreground">
                    {label}
                  </p>
                  {value && charLimit != null && (
                    <MetaCharCount value={value} max={charLimit} />
                  )}
                  {aiField && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">
                      <Sparkles size={10} />
                      AI
                    </span>
                  )}
                </div>
                {value ? (
                  <p className="mt-0.5 text-foreground break-all leading-relaxed">
                    {value}
                  </p>
                ) : (
                  <p className="mt-0.5 text-muted-foreground italic">
                    Not set
                  </p>
                )}
                {check?.message && !hideAuditIssues && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {check.message}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function SocialPreview({
  socialMeta,
  url,
  aiModel = DEFAULT_AI_MODEL,
  setAiModel,
  aiDisabled = false,
  knownIssues,
  seoScore,
}) {
  const [aiTags, setAiTags] = useState(null);
  const [metaView, setMetaView] = useState("original");
  const [improving, setImproving] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const { isAuthenticated, token } = useAuth();
  const { openAuthModal } = useAuthModal();

  const tags = socialMeta?.tags;
  const resolved = socialMeta?.resolved;
  const checks = socialMeta?.checks ?? [];
  const counts = socialMeta?.counts ?? { pass: 0, warn: 0, error: 0 };
  const fetchError = socialMeta?.fetchError;
  const pageContext = socialMeta?.pageContext;

  const needsFix = counts.warn + counts.error > 0;

  useEffect(() => {
    setAiTags(null);
    setMetaView("original");
  }, [url]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setAiUsage(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/metrics/ai-usage`,
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) {
          setAiUsage(response.data?.data?.aiUsage ?? null);
        }
      } catch {
        // non-critical
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token]);

  const handleImprove = useCallback(async () => {
    if (aiDisabled || improving || !tags || !resolved) return;

    if (!isAuthenticated || !token) {
      openAuthModal("auth");
      return;
    }

    if (aiUsage && !aiUsage.unlimited && aiUsage.remaining === 0) {
      toast.error(
        `Daily meta improve limit reached (${aiUsage.limit}/day). Try again tomorrow.`,
      );
      return;
    }

    setImproving(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/metrics/improve-social-meta`,
        {
          tags,
          resolved,
          url,
          aiModel,
          pageContext,
          seoContext: buildSeoContext(knownIssues, seoScore, checks),
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const improved = response.data?.data?.improved;
      if (!improved) {
        throw new Error("No improved meta returned");
      }
      setAiTags(improved);
      setMetaView("ai");
      if (response.data?.data?.aiUsage) {
        setAiUsage(response.data.data.aiUsage);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        openAuthModal("auth");
        return;
      }
      if (err.response?.status === 429) {
        if (err.response?.data?.data?.aiUsage) {
          setAiUsage(err.response.data.data.aiUsage);
        }
      }
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to improve meta tags";
      toast.error(msg);
    } finally {
      setImproving(false);
    }
  }, [
    aiDisabled,
    improving,
    tags,
    resolved,
    isAuthenticated,
    token,
    openAuthModal,
    url,
    aiModel,
    pageContext,
    knownIssues,
    seoScore,
    checks,
    aiUsage,
  ]);

  const activeTags = useMemo(() => {
    if (!tags) return {};
    if (metaView === "ai" && aiTags) return mergeSocialTags(tags, aiTags);
    return tags;
  }, [metaView, aiTags, tags]);

  const activeResolved = useMemo(() => {
    if (!resolved) return null;
    return buildResolvedFromTags(resolved, activeTags);
  }, [resolved, activeTags]);

  const hideAuditIssues = Boolean(aiTags) && metaView === "ai";

  if (!socialMeta) {
    return (
      <p className="text-center text-muted-foreground py-8">
        Social preview data is not available for this test. Run a new analysis
        to inspect Open Graph and Twitter meta tags.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {fetchError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Could not fetch page HTML ({fetchError}). Preview uses fallback values
          where tags are missing.
        </div>
      )}

      {!hideAuditIssues && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <CountBadge status="pass" count={counts.pass} />
          <CountBadge status="warn" count={counts.warn} />
          <CountBadge status="error" count={counts.error} />
        </div>
      )}

      <SocialAiBanner
        needsFix={needsFix}
        improving={improving}
        aiTags={aiTags}
        aiDisabled={aiDisabled}
        isAuthenticated={isAuthenticated}
        aiUsage={aiUsage}
        onFix={handleImprove}
        metaView={metaView}
        onMetaViewChange={setMetaView}
      />

      <PlatformPreviewTabs resolved={activeResolved || resolved} tags={activeTags} />

      {resolved.url && (
        <p className="text-center text-xs text-muted-foreground">
          Canonical URL:{" "}
          <a
            href={resolved.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:underline inline-flex items-center gap-1"
          >
            {resolved.url}
            <ExternalLink size={12} />
          </a>
        </p>
      )}

      <MetaInspector
        tags={activeTags}
        checks={checks}
        baseTags={tags}
        aiTags={aiTags}
        metaView={metaView}
        aiDisabled={aiDisabled}
        aiModel={aiModel}
        onChangeModel={
          setAiModel ? () => setModelModalOpen(true) : null
        }
        onRewrite={handleImprove}
        improving={improving}
        aiUsage={aiUsage}
        isAuthenticated={isAuthenticated}
        hideAuditIssues={hideAuditIssues}
      />

      {setAiModel && (
        <AiModelModal
          isOpen={modelModalOpen}
          onClose={() => setModelModalOpen(false)}
          aiModel={aiModel}
          setAiModel={setAiModel}
        />
      )}

      {url && (
        <p className="text-center text-xs text-muted-foreground">
          Parsed from the tested page. Some platforms cache previews, so changes
          may take time to appear after you update meta tags.
        </p>
      )}
    </div>
  );
}
